import { SelectionMenuViewMode } from '@components/ButtonBaseWithSelectionMenu/types/buttonBaseWithSelectionMenu.types';
import * as React from 'react';
import { useCallback } from 'react';
import ButtonBaseWithSelectionMenu from '../../../../../components/ButtonBaseWithSelectionMenu/ButtonBaseWithSelectionMenu';
import useWidth from '../../../../../components/Hooks/useWidth';
import ProductAvailabilityChip from '../../../../../components/Product/ProductAvailabilityChip';
import LangEnum from '../../../../../lang/LangEnum';
import { OrderFormTestIds } from '../../../../../utils/testIds/OrderForm.testIds';
import { AvailabilityEnum, DeliveryWindow } from '../../../../products/ProductTypes';
import { useTranslation } from '../../../../translation/TranslationContext';
import { useOrderContext } from '../../../contexts/Order.context';
import { ORDER_PRODUCT_SELECTION_MENU_CONFIG } from '../../orderProducts.constants';
import { OrderProductListViewType } from '../../orderProducts.types';
import {
  HandleSelectItemNewDeliveryWindow,
  HandleSelectItemNewDeliveryWindowParams,
} from '../types/useOrderProductItem.types';
import OrderProductButtonContent from './OrderProductButtonContent';
import OrderProductDeliveryWindowsMenu from './OrderProductDeliveryWindowsMenu';

interface DeliveryWindowButtonContentProps {
  isPlainPlaceholder: boolean;
  label: string;
  availability: AvailabilityEnum;
  disabled: boolean;
}

const DeliveryWindowButtonContent = ({
  isPlainPlaceholder,
  label,
  availability,
  disabled,
}: DeliveryWindowButtonContentProps) => {
  if (isPlainPlaceholder) {
    return <OrderProductButtonContent label={label} disabled={disabled} />;
  }

  return (
    <OrderProductButtonContent
      disabled={disabled}
      startAdornment={
        <ProductAvailabilityChip availability={availability} label={label} disabled={disabled} />
      }
    />
  );
};

interface OrderProductDeliveryWindowProps {
  deliveryWindow:
    | DeliveryWindow
    | {
        id: string;
        name: string;
      }
    | undefined;
  availability: AvailabilityEnum;
  viewMode: OrderProductListViewType;
  productId?: string;
  productColorId?: string;
  disabled?: boolean;
  canEditOrder?: boolean;
  isLoadingButton?: boolean;
  isFocusable?: boolean;
  hasPlainEmptyState?: boolean;
  label?: LangEnum;
  testId?: string;
  onSelectNewDeliveryWindow: HandleSelectItemNewDeliveryWindow;
}

const OrderProductDeliveryWindow = ({
  viewMode,
  productId,
  availability,
  canEditOrder,
  deliveryWindow,
  productColorId,
  onSelectNewDeliveryWindow,
  disabled = false,
  isLoadingButton = true,
  isFocusable = false,
  hasPlainEmptyState = false,
  label = LangEnum.GLOBAL_TO_DEFINE_DELIVERY_SHORT,
  testId = OrderFormTestIds.SELECT_DELIVERY_WINDOW_BUTTON,
}: OrderProductDeliveryWindowProps) => {
  const { mobileWidth } = useWidth();
  const { order } = useOrderContext();
  const { translate } = useTranslation();
  const { removeCopiedGrid } = useOrderContext();

  const disabledButton = disabled || !canEditOrder || viewMode === SelectionMenuViewMode.VIEW;

  const orderEditPage = viewMode === SelectionMenuViewMode.EDIT;

  const handleSelectNewDeliveryWindow: HandleSelectItemNewDeliveryWindow = useCallback(
    ({ availability: newAvailability, lastAvailability, newDwAvailability }) => {
      onSelectNewDeliveryWindow({
        availability: newAvailability,
        newDwAvailability,
        lastAvailability,
      });
      removeCopiedGrid();
    },
    [onSelectNewDeliveryWindow, removeCopiedGrid],
  );

  const canRenderMenu = orderEditPage && !disabledButton && !!order;

  const isCurrentSelection = ({
    availability: newAvailability,
    newDwAvailability,
  }: HandleSelectItemNewDeliveryWindowParams) =>
    newAvailability === availability &&
    newDwAvailability?.deliveryWindow?.id === deliveryWindow?.id;

  const isReadyForDelivery = availability === AvailabilityEnum.READY_FOR_DELIVERY;
  const deliveryWindowName = deliveryWindow?.name ?? translate(label);

  const customLabel = isReadyForDelivery
    ? translate(LangEnum.GLOBAL_READY_FOR_DELIVERY)
    : deliveryWindowName;

  const showPlainPlaceholder = hasPlainEmptyState && !deliveryWindow && !isReadyForDelivery;

  return (
    <ButtonBaseWithSelectionMenu
      viewMode={viewMode}
      disabled={isLoadingButton || !canRenderMenu}
      isLoadingButton={isLoadingButton}
      isFocusable={isFocusable}
      menuConfig={ORDER_PRODUCT_SELECTION_MENU_CONFIG}
      disabledTooltipLabel={
        mobileWidth && viewMode === SelectionMenuViewMode.VIEW ? customLabel : undefined
      }
      testId={testId}
      buttonContent={
        <DeliveryWindowButtonContent
          isPlainPlaceholder={showPlainPlaceholder}
          label={customLabel}
          availability={availability}
          disabled={disabledButton}
        />
      }
      renderMenuContent={({ closeMenu, anchorEl, updatePosition }) => (
        <OrderProductDeliveryWindowsMenu
          deliveryWindow={deliveryWindow}
          updatePosition={updatePosition}
          onClick={(params) => {
            if (!isCurrentSelection(params)) {
              handleSelectNewDeliveryWindow(params);
            }

            closeMenu();
          }}
          productId={productId}
          productColorId={productColorId}
          availability={availability}
          anchorEl={anchorEl}
        />
      )}
    />
  );
};

export default React.memo(OrderProductDeliveryWindow);
